import type { Jurisdiction } from '@/repositories/JurisdictionRepository';
import { LogicElement, LogicGroup, LogicOperator } from '@/types/logic';

export const JURISDICTION_ELIGIBILITY_FIELD = 'active_classification_keys';
const JURISDICTION_KEY_PREFIX = 'jurisdiction.';

// Mirrors the backend's JurisdictionPersonDynamicRule._jurisdiction_slug
// (backend/homebase/classifications/classifiers/person_classifier/person_rules/jurisdiction_person_dynamic_rule.py):
// the classification key materialized for a jurisdiction named e.g. "New
// York" is "jurisdiction.new_york".
export const jurisdictionClassificationKey = (name: string) =>
  `${JURISDICTION_KEY_PREFIX}${name.toLowerCase().replace(/ /g, '_')}`;

export const elementKey = (element: LogicElement): string | null =>
  Array.isArray(element.value) && element.value.length === 1 && typeof element.value[0] === 'string'
    ? element.value[0]
    : null;

export const isJurisdictionElement = (element: LogicElement) =>
  element.field === JURISDICTION_ELIGIBILITY_FIELD &&
  !!elementKey(element)?.startsWith(JURISDICTION_KEY_PREFIX);

export const isJurisdictionGroup = (group: LogicGroup) =>
  group.logic_operator === LogicOperator.OR &&
  group.logic_groups.length === 0 &&
  group.logic_elements.every(isJurisdictionElement);

// Reads the jurisdiction ids currently selected out of an eligibility tree --
// the read half of the two-way binding in useJurisdictionEligibility, shared
// with any other consumer (e.g. a read-only details view) that only needs to
// display the selection, not edit it.
export const getSelectedJurisdictionIds = (
  rootGroups: LogicGroup[],
  jurisdictions: Jurisdiction[]
): string[] => {
  const keyToId = new Map(
    jurisdictions.map((jurisdiction) => [
      jurisdictionClassificationKey(jurisdiction.name),
      jurisdiction.id
    ])
  );

  const jurisdictionGroup = rootGroups.find(isJurisdictionGroup);
  const existingKeys =
    jurisdictionGroup?.logic_elements
      .map(elementKey)
      .filter((key): key is string => key !== null) ?? [];

  return existingKeys.map((key) => keyToId.get(key)).filter((id): id is string => id !== undefined);
};
